// Constants
export const ADVISOR_NAME = 'Nattapong';
export const CUSTOMER_NAME = 'Siriya';
export const COMPANY_NAME = 'Chubb Life Thailand';
export const SCRIPTED_AGENT_ID = 'agent_01jzqbwk0yf9vs71hrgbqxexfr';

export interface ScriptNode {
  id: string;
  type: 'greeting' | 'statement' | 'question' | 'closing' | 'decision';
  content: string;
  agentMessage: string;
  expectedResponse?: {
    positive?: string[];
    negative?: string[];
    neutral?: string[];
  };
  nextNodes?: {
    positive?: string;
    negative?: string;
    neutral?: string;
    default?: string;
  };
  isEnd?: boolean;
  backgroundColor?: string;
  requiresInput?: boolean;
  waitForResponse?: boolean;
}

export interface Script {
  id: string;
  name: string;
  description: string;
  startNode: string;
  nodes: Record<string, ScriptNode>;
  agentInfo?: {
    name: string;
    voiceId: string;
    avatar?: string;
  };
}

export const chubbPaymentReminderScript: Script = {
  id: 'chubb-payment-reminder',
  name: 'Chubb Life Insurance - Courtesy Payment Reminder',
  description:
    'Virtual assistant calling on behalf of insurance advisor for annual premium payment reminder and promotions',
  startNode: '1',
  agentInfo: {
    name: 'Maya',
    voiceId: 'PKtykp4ltYtZziN4XTKn',
    avatar:
      'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp',
  },
  nodes: {
    '1': {
      id: '1',
      type: 'greeting',
      content: 'Initial greeting and introduction',
      agentMessage: `Hello ${CUSTOMER_NAME}. This is a courtesy call from ${COMPANY_NAME}. I'm an AI virtual assistant representing your insurance advisor, Khun ${ADVISOR_NAME}. Before we begin, may I kindly inform you that this call is recorded for service quality purposes. I'm calling today regarding your life insurance plan with us. Your next annual premium payment will be due in about one month. Do you have 5 minutes for me to share some details?`,
      backgroundColor: '#d4edda',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'sure', 'okay', 'go ahead', 'fine', 'i have time'],
        negative: ['no', 'busy', 'not now', 'later', 'bad time'],
        neutral: ['unclear', 'what', 'sorry', "can't hear"],
      },
      nextNodes: {
        positive: '2',
        negative: '8',
        neutral: '9',
        default: '2',
      },
    },
    '2': {
      id: '2',
      type: 'statement',
      content: 'Introduce payment reminder and options',
      agentMessage: `Thank you, Khun ${CUSTOMER_NAME}. Your policy's annual premium is scheduled for payment next month. This call is just a friendly reminder so you have ample time to prepare. For your convenience, Chubb offers multiple payment methods in Thailand: Online payment via Chubb Life's eService app or website, Internet banking with major banks like Bangkok Bank, Kasikorn, Siam Commercial, and Krungthai, Payment at 7-Eleven counters nationwide, and Auto-debit from your registered bank account or credit card. Would you like me to share more about any of these payment channels?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'sure', 'interested', 'tell me more', 'explain'],
        negative: ['no', 'not needed', 'i know', 'no thanks'],
      },
      nextNodes: {
        positive: '3',
        negative: '6',
        default: '3',
      },
    },
    '3': {
      id: '3',
      type: 'statement',
      content: 'Explain payment options in detail',
      agentMessage: `Of course. Many clients find the auto-debit option easiest, since it ensures no missed deadlines and gives peace of mind. Alternatively, if you prefer flexibility, the eService app allows real-time payment with an instant receipt. 7-Eleven counter payments are also popular for convenience, especially if you're out and about. Which option would you find most useful for your upcoming premium?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: [
          'auto-debit',
          'eservice',
          '7-eleven',
          'banking',
          'app',
          'online',
        ],
        neutral: ['unsure', 'not sure', "don't know", 'need to think'],
      },
      nextNodes: {
        positive: '4',
        neutral: '7',
        default: '4',
      },
    },
    '4': {
      id: '4',
      type: 'question',
      content: 'Acknowledge choice and offer reminder service',
      agentMessage: `Thank you, Khun ${CUSTOMER_NAME}. I've noted that you prefer this method. For added support, Chubb Life also has a reminder system — we can send you a text message or email a few days before the deadline, to ensure peace of mind. Would you like to activate this reminder service?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'sure', 'activate', 'okay'],
        negative: ['no', 'not needed', 'no thanks'],
      },
      nextNodes: {
        positive: '5',
        negative: '5',
        default: '5',
      },
    },
    '5': {
      id: '5',
      type: 'closing',
      content: 'Final confirmation and promotion offer',
      agentMessage: `Perfect, Khun ${CUSTOMER_NAME}. Your payment reminder preference is noted. Before I let you go, I'd also like to share that right now, Chubb Life is offering limited-time add-on promotions for valued clients. These include enhanced accident coverage and health rider options with preferred premium rates. If you're interested, I can arrange for Khun ${ADVISOR_NAME}, your personal advisor, to call you directly and explain these in detail. Would you like me to notify him to call you back at your preferred time?`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'interested', 'sure', 'sounds good'],
        negative: ['no', 'not interested', 'no thanks'],
        neutral: ['hesitant', 'maybe', 'not sure', 'need to think'],
      },
      nextNodes: {
        positive: '14',
        negative: '10',
        neutral: '7',
        default: '10',
      },
    },
    '6': {
      id: '6',
      type: 'statement',
      content: 'Handle initial rejection of payment details',
      agentMessage: `I understand, Khun ${CUSTOMER_NAME}. Just to reassure you, the payment can be done in the way most convenient for you — whether through mobile banking, card auto-debit, or even cash at 7-Eleven. Our goal is simply to help ensure your coverage remains uninterrupted. Would you like me to also share about the special add-on promotions available this month?`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['maybe', 'yes', 'okay', 'tell me'],
        negative: ['no', 'still no', 'not interested'],
      },
      nextNodes: {
        positive: '3',
        negative: '10',
        default: '10',
      },
    },
    '7': {
      id: '7',
      type: 'statement',
      content: 'Address hesitation',
      agentMessage: `I completely understand, Khun ${CUSTOMER_NAME}. It can be a lot to consider. If you'd like, I can send you a summary of the payment channels and promotions to your email for review. Alternatively, I can arrange for Khun ${ADVISOR_NAME} to call you personally and explain. Which would you prefer?`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['email', 'send info', 'summary'],
        negative: ['advisor', 'call', 'personal call'],
        neutral: ['no', 'neither', 'not interested'],
      },
      nextNodes: {
        positive: '11',
        negative: '14',
        neutral: '10',
        default: '10',
      },
    },
    '8': {
      id: '8',
      type: 'question',
      content: 'Busy prospect callback',
      agentMessage: `No problem, Khun ${CUSTOMER_NAME}. This call was just to remind you of your premium due date and mention the limited-time promotions. Would it be better if I call you back at a more convenient time?`,
      backgroundColor: '#ffffcc',
      requiresInput: true,
      waitForResponse: true,
      expectedResponse: {
        positive: ['yes', 'call back', 'later', 'better time'],
        negative: ['no', "don't call", 'not needed'],
      },
      nextNodes: {
        positive: '12',
        negative: '10',
        default: '10',
      },
    },
    '9': {
      id: '9',
      type: 'closing',
      content: 'Hearing issues / connection problem',
      agentMessage: `Sorry, I'm having some trouble hearing you clearly. If it's okay, I can try calling back another time just to remind you of your premium due next month and share updates from Chubb. Thank you for your time today, and I wish you a pleasant day.`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
    '10': {
      id: '10',
      type: 'closing',
      content: 'Polite rejection closing',
      agentMessage: `No worries at all, Khun ${CUSTOMER_NAME}. Thank you for taking the time to speak with me today. If you need any support with your policy payment or would like to explore promotions in the future, please don't hesitate to reach out to Chubb Life or Khun ${ADVISOR_NAME} directly. Have a wonderful day.`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
    '11': {
      id: '11',
      type: 'closing',
      content: 'Send information promise',
      agentMessage: `Got it, Khun ${CUSTOMER_NAME}. We'll email you details of the available payment options and promotions, and copy Khun ${ADVISOR_NAME}. You may simply reply whenever you're ready. Thank you very much for your time, and have a lovely day.`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
    '12': {
      id: '12',
      type: 'question',
      content: 'Schedule callback time',
      agentMessage: `Great. When would be a better time for me to call you back to remind you about your premium and promotions? For example, tomorrow morning or later in the week.`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      nextNodes: {
        default: '13',
      },
    },
    '13': {
      id: '13',
      type: 'closing',
      content: 'Callback confirmation',
      agentMessage: `Thank you, Khun ${CUSTOMER_NAME}. I'll arrange to call you back then. I appreciate your time, and wish you a pleasant day.`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
    '14': {
      id: '14',
      type: 'question',
      content: 'Schedule advisor callback',
      agentMessage: `Excellent. In the next 3 days, when would it be most convenient for Khun ${ADVISOR_NAME} to call you? For example, tomorrow after 6pm, or Friday morning.`,
      backgroundColor: '#ffffff',
      requiresInput: true,
      waitForResponse: true,
      nextNodes: {
        default: '15',
      },
    },
    '15': {
      id: '15',
      type: 'closing',
      content: 'Advisor callback confirmation',
      agentMessage: `Perfect. I'll share this schedule with Khun ${ADVISOR_NAME} so he can reach out to you directly. Thank you, Khun ${CUSTOMER_NAME}, for your time today. We truly value your trust with Chubb Life. Have a wonderful day ahead.`,
      backgroundColor: '#ffcccc',
      isEnd: true,
      requiresInput: false,
    },
  },
};
